import { useAiWriter } from '@/hooks/useAiWriter'
import { getSearchData } from '@/services/apis/aiwriter'
import { ELKReportName } from '@/types'
import { DataEvent, OperateType } from '@/types/aiAsk'
import { IAIOutline, IAIOutlineRequestStatus, IAIWritingPrompt, IArticleTextData, IDataBase, IWritingStep, TextType } from '@/types/aiWriting'
import { elkReport } from '@/utils'
import { chalkConsole, createStreamIterator, formatTime } from '@/utils/outline'
import bluebird from 'bluebird'
import chalk from 'chalk'
import { cloneDeep } from 'lodash'
import { useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { useModel } from 'umi'
import { v4 as uuidV4 } from 'uuid'

const useAIWritingModel = () => {
  const { currentCharacter, initCharacterSetting } = useModel('aiCharacter') // 人设Model

  const [writingStep, setWritingStep] = useState<IWritingStep>(IWritingStep.promopt) // 流程步骤
  const [aiWritingLoadding, setAiWritingLoadding] = useState(true)
  const [leftIsEmpty, setLeftIsEmpty] = useState(false)
  const [promopt, setPromopt] = useState<IAIWritingPrompt>({ title: '', tone: 'neutral' })
  const [dataBaseId, setDataBaseId] = useState('') // 知识库的traceId
  const [dataBaseList, setDataBaseList] = useState<IDataBase[]>([]) // 知识库数据
  const [extraDB, setExtraDB] = useState({
    // 用户自己补充的知识库
    id: '',
    title: '',
    content: '',
  })
  const [aiOutlines, setAIOutlines] = useState<IAIOutline[]>([]) // 大纲数据
  const [aiOutlineRequestStatus, setAiOutlineRequestStatus] = useState<IAIOutlineRequestStatus>({})
  const [showOrderPop, setShowOrderPop] = useState(false) // 排序弹窗

  const stopAIRequestRef = useRef(false) // 是否终止AI流式请求，为了在手动close接口时，让缓存池的定时遍历停止
  const aiOutlineRefs = useRef(aiOutlines) // outlines的引用，在某些特殊情况下使用，防止outlines的状态无法改变造成的问题
  const aiOutlineRequestStatusRef = useRef(aiOutlineRequestStatus)
  const abortController = useRef(new AbortController()) // 请求中断控制器
  const outlinePromptInfo = useRef({
    prompt: '',
    aiModelTitle: '',
  }) // 获取大纲时返回的Prompt与大纲数据

  const { ask: outlineAsk, close: closeOutlineAsk } = useAiWriter({ dependOnUser: true }) // 该ask与close转用于大纲的流式请求

  const commonIntervalTime = 20 // 通用定时时间
  const batchSize = 15 // 设置批量处理的步长
  let beginTime: any, endTime: any

  // 更新ai writing流程的prompt
  const updatePrompt = (
    param: Partial<IAIWritingPrompt>,
    option?: {
      isFlushSync?: boolean
    },
  ) => {
    if (option?.isFlushSync) {
      // 强制同步更新
      flushSync(() => {
        setPromopt({
          ...promopt,
          ...param,
        })
      })
      return
    }

    setPromopt({
      ...param,
      ...promopt,
    })
  }

  // 更新请求状态
  const updateAIOutlineRequestStatusRef = (option: IAIOutlineRequestStatus) => {
    aiOutlineRequestStatusRef.current = {
      ...aiOutlineRequestStatusRef.current,
      ...option,
    }
    setAiOutlineRequestStatus(cloneDeep(aiOutlineRequestStatusRef.current))
  }

  /**
   * 统一更新大纲数据
   * 由于数据更新较为频繁，在此处统一更新aiOutlines和aiOutlineRefs
   * aiOutlineRefs不应该依赖aiOutlines进行更新，由于数据交叉更新，会导致数据更新异常
   */
  const updateAIOutlines = (list: IAIOutline[]) => {
    const res = cloneDeep(list)
    setAIOutlines(res)
    aiOutlineRefs.current = res
  }

  // 获取大纲
  const getAIOutlineList = () => {
    let timer: any
    stopAIRequestRef.current = false // 从获取大纲开始，重置为false
    console.log(chalk.green('开始AI生成大纲标题'))
    beginTime = performance.now()
    updateAIOutlineRequestStatusRef({
      outlineLoading: true,
      outlineLoaded: false,
      titleLoading: true,
    })

    return new Promise((resolve) => {
      let currentIndex = 0
      const cacheList: IArticleTextData[] = []
      const iterrator = createStreamIterator(cacheList, batchSize)
      const iteratorBeginTime = performance.now()

      const askMsgCallback = (data: IArticleTextData) => {
        if (data.type === TextType.PROMPT) {
          // 保存获取大纲时返回的Prompt
          outlinePromptInfo.current.prompt = data.text
          return
        }
        cacheList.push(data)
        outlinePromptInfo.current.aiModelTitle += data.text || ''
      }

      const getByInterval_outline = () => {
        timer = setInterval(() => {
          const { value, done } = iterrator.next()
          // aiOutlineRefs.current的引用地址可能会被改变，所以要重新获取
          const newOutline = aiOutlineRefs.current

          if (done || stopAIRequestRef.current) {
            updateAIOutlineRequestStatusRef({
              titleLoading: false,
              titleLoaded: true,
            })
            clearInterval(timer)
            // 如果最后一条大纲内容为空，删掉
            if (!newOutline[currentIndex].title) {
              newOutline.pop()
              updateAIOutlines(newOutline)
            }
            resolve('')
            const iteratorEndTime = performance.now()
            chalkConsole('大纲迭代处理耗时时间：', formatTime(iteratorBeginTime, iteratorEndTime))
            return
          }

          if (!value || value.length === 0) {
            return
          }

          let shouldUpdate = false
          value.forEach((data) => {
            if (/^\n+$/.test(data.text)) {
              // 根据换行符，要新增一条大纲
              currentIndex++
              newOutline[currentIndex] = {
                id: uuidV4(),
                title: '',
                aiModelTitle: '',
                questionPrompt: '',
                aiModelQuestion: '',
                concepts: [],
              }
              aiOutlineRequestStatusRef.current.titleLoadingPosition = currentIndex
              setAiOutlineRequestStatus(cloneDeep(aiOutlineRequestStatusRef.current))
            } else {
              if (!newOutline[currentIndex]) {
                newOutline[currentIndex] = {
                  id: uuidV4(),
                  title: '',
                  aiModelTitle: '',
                  questionPrompt: '',
                  aiModelQuestion: '',
                  concepts: [],
                }
              }

              newOutline[currentIndex].title += data.text
              newOutline[currentIndex].aiModelTitle += data.text
              shouldUpdate = true
            }
          })

          if (shouldUpdate) {
            updateAIOutlines(newOutline)
          }
        }, commonIntervalTime)
      }

      const askCloseCb_outline = () => {
        endTime = performance.now()
        iterrator.markStreakClosed()
        chalkConsole('大纲流式接口耗时时间：', formatTime(beginTime, endTime))
        elkReport({
          name: ELKReportName.aiWritingInfo,
          message: `大纲标题生成耗时：${formatTime(beginTime, endTime)}`,
          grade: 'Info',
          category: 'promise',
        })
        console.log(chalk.blue('完成大纲标题生成'))
        console.log(chalk.bgCyanBright('大纲缓存'), cacheList)
      }

      outlineAsk(
        {
          title: promopt.title,
          tone: promopt.tone as any,
          operateType: OperateType.GENERATE_OUTLINE,
          traceId: dataBaseId,
          character: currentCharacter?.name,
        },
        {
          openCb: getByInterval_outline,
          msgCb: askMsgCallback,
          closeCb: askCloseCb_outline,
        },
      )
    })
  }

  // 批量获取大纲的写文思路
  const getAIOutlineConceptByBatch = async () => {
    console.log(chalk.green('开始获取写文思路'))
    beginTime = performance.now()
    updateAIOutlineRequestStatusRef({
      conceptLoading: true,
      conceptLoaded: false,
    })

    await bluebird.map(aiOutlineRefs.current, async (item, idx) => {
      // 获取单个大纲的写作问题
      await getAIConceptBySingleOutline(idx)
    })

    endTime = performance.now()
    chalkConsole('写文思路获取耗时时间：', formatTime(beginTime, endTime))
    elkReport({
      name: ELKReportName.aiWritingInfo,
      message: `写文思路生成耗时：${formatTime(beginTime, endTime)}`,
      grade: 'Info',
      category: 'promise',
    })
    console.log(chalk.blue('完成写文思路获取'))
    updateAIOutlineRequestStatusRef({
      conceptLoaded: true,
      conceptLoading: false,
    })
  }

  // 获取单个大纲的写文思路
  const getAIConceptBySingleOutline = (outlineIndex: number) => {
    const cacheList: IArticleTextData[] = []
    let timer: any
    aiOutlineRefs.current[outlineIndex].aiModelQuestion = '' // 先清空之前的问题
    const { ask } = aiOutlineRefs.current[outlineIndex].rqInfo || {}
    if (!ask) {
      return
    }

    return new Promise((resolve) => {
      let conceptIndex = 0
      const iterator = createStreamIterator(cacheList, batchSize)

      const askMsgCallback = (data: IArticleTextData) => {
        if (data?.type === TextType.PROMPT) {
          // 保存大纲获取写文思路时返回的Prompt
          aiOutlineRefs.current[outlineIndex].questionPrompt = data.text
          return
        }
        cacheList.push(data)
      }

      const askCloseCb = () => {
        iterator.markStreakClosed()
        console.log(chalk.blue('完成单个大纲写文思路生成'))
        console.log(chalk.bgCyanBright('写文思路缓存'), cacheList)
      }

      const getDataInterval = () => {
        timer = setInterval(() => {
          //  aiOutlineRefs.current的引用地址可能会被改变，所以要重新获取
          const { concepts } = aiOutlineRefs.current[outlineIndex]
          const { value, done } = iterator.next() as {
            value: IArticleTextData[] | undefined
            done: boolean
          }

          if (done || stopAIRequestRef.current) {
            clearInterval(timer)
            resolve('')
            // 如果最后一条写作问题内容为空，删掉
            if (!concepts[conceptIndex].question) {
              concepts.pop()
              updateAIOutlines(aiOutlineRefs.current)
            }
            return
          }

          if (aiOutlineRefs.current.length === 0) {
            // 防止突然退出AI生成，做一次兼容
            return
          }

          if (!value || value.length === 0) {
            return
          }

          let shouldUpdate = false

          value.forEach((data) => {
            // 根据换行符，要新增一条写作问题
            if (/^\n+$/.test(data.text)) {
              conceptIndex++
              // 防止连续出现换行符导致数据处理异常
              concepts[conceptIndex] = {
                id: uuidV4(),
                question: '',
                answerLoading: true,
                answerLoaded: false,
              }
              shouldUpdate = true
            } else {
              if (!concepts[conceptIndex]) {
                concepts[conceptIndex] = {
                  id: uuidV4(),
                  question: '',
                  answerLoading: true,
                  answerLoaded: false,
                }
              }
              concepts[conceptIndex].question += data.text
              aiOutlineRefs.current[outlineIndex].aiModelQuestion += data.text // 保存AI生成的问题
              shouldUpdate = true
            }
          })

          if (shouldUpdate) {
            updateAIOutlines(aiOutlineRefs.current)
          }
        }, commonIntervalTime)
      }

      ask(
        {
          title: promopt.title,
          tone: promopt.tone as any,
          outline: aiOutlineRefs.current[outlineIndex].title,
          operateType: OperateType.GENERATE_QUESTION,
          traceId: dataBaseId,
          character: currentCharacter?.name,
        },
        {
          openCb: getDataInterval,
          msgCb: askMsgCallback,
          closeCb: askCloseCb,
        },
      )
    })
  }

  // 批量获取所有大纲的写文思路的取数
  const getConceptAnswerByBatch = async () => {
    console.log(chalk.green('开始取数'))
    beginTime = performance.now()
    updateAIOutlineRequestStatusRef({
      conceptLoading: false,
      conceptLoaded: true,
      referenceLoaded: false,
      referenceLoading: true,
    })

    // 并发处理
    await bluebird.map(aiOutlineRefs.current, async (item, idx) => {
      await bluebird.map(item.concepts, async (concept, conceptIndex) => {
        await getSingleConceptAnswer(idx, conceptIndex)
      })
    })

    endTime = performance.now()
    chalkConsole('取数整体耗时时间：', formatTime(beginTime, endTime))
    elkReport({
      name: ELKReportName.aiWritingInfo,
      message: `取数整体耗时：${formatTime(beginTime, endTime)}`,
      grade: 'Info',
      category: 'promise',
    })
    console.log(chalk.blue('完成取数'))
  }

  // 获取单个写文思路的取数
  const getSingleConceptAnswer = async (outlineIndex: number, conceptIndex: number) => {
    const cacheList: string[] = []
    const askMsgCallback = (data: DataEvent) => {
      cacheList.push(data.text || '')
    }

    const currnetConcept = aiOutlineRefs.current[outlineIndex].concepts[conceptIndex]
    const { ask } = currnetConcept.rqInfo || {}

    if (!ask) {
      return
    }

    return new Promise(async (resolve) => {
      const askCloseCb = () => {
        const data = aiOutlineRefs.current[outlineIndex].concepts[conceptIndex]
        aiOutlineRefs.current[outlineIndex].concepts[conceptIndex] = {
          ...data,
          answer: cacheList.join(''),
          answerStatus: true,
          answerLoaded: true,
          answerLoading: false,
        }
        setAIOutlines(cloneDeep(aiOutlineRefs.current))
        resolve('')
      }

      ask(
        {
          title: promopt.title,
          tone: promopt.tone as any,
          outline: aiOutlineRefs.current[outlineIndex].title,
          question: aiOutlineRefs.current[outlineIndex].concepts[conceptIndex].question,
          operateType: OperateType.ANALYSIS_AND_PROCESS, // 获取写文思路取数
          traceId: dataBaseId,
          character: currentCharacter?.name,
        },
        {
          msgCb: askMsgCallback,
          closeCb: askCloseCb,
        },
      )
    })
  }

  // 编辑大纲标题后的重新生成
  const generateNewAIOutline = async (outlineIndex: number) => {
    setAiWritingLoadding(true)
    updateAIOutlineRequestStatusRef({
      outlineLoading: true,
      outlineLoaded: false,
      conceptLoaded: false,
      conceptLoading: true,
      referenceLoading: false,
      referenceLoaded: false,
    })

    console.log(chalk.green(`开始获取大纲${outlineIndex + 1}的写文思路`))
    beginTime = performance.now()
    // 重新生成写文思路
    await getAIConceptBySingleOutline(outlineIndex)
    endTime = performance.now()
    chalkConsole('写文思路获取耗时时间：', formatTime(beginTime, endTime))
    elkReport({
      name: ELKReportName.aiWritingInfo,
      message: `写文思路获取耗时：${formatTime(beginTime, endTime)}`,
      grade: 'Info',
      category: 'promise',
    })
    console.log(chalk.blue('完成写文思路获取'))

    updateAIOutlineRequestStatusRef({
      conceptLoading: false,
      conceptLoaded: true,
      referenceLoaded: false,
      referenceLoading: true,
    })
    const concepts = aiOutlineRefs.current[outlineIndex].concepts
    // 并发获取写文思路的取数
    console.log(chalk.green('开始取数'))
    beginTime = performance.now()
    await bluebird.map(concepts, async (concept, conceptIndex) => {
      await getSingleConceptAnswer(outlineIndex, conceptIndex)
    })
    endTime = performance.now()
    chalkConsole('取数整体耗时时间：', formatTime(beginTime, endTime))
    elkReport({
      name: ELKReportName.aiWritingInfo,
      message: `取数整体耗时：${formatTime(beginTime, endTime)}`,
      grade: 'Info',
      category: 'promise',
    })

    updateAIOutlineRequestStatusRef({
      outlineLoading: false,
      outlineLoaded: true,
      referenceLoaded: true,
      referenceLoading: false,
    })
    setAiWritingLoadding(false)
    console.log(chalk.blue('完成大纲编辑后的重新生成'))
  }

  //  编辑某个写问思路后的重新生成
  const generateNewConceptAnswer = async (outlineIndex: number, conceptIndex: number) => {
    setAiWritingLoadding(true)
    updateAIOutlineRequestStatusRef({
      outlineLoading: true,
      outlineLoaded: false,
      referenceLoading: true,
      referenceLoaded: false,
    })

    console.log(chalk.green('开始取数'))
    beginTime = performance.now()
    await getSingleConceptAnswer(outlineIndex, conceptIndex)
    endTime = performance.now()
    chalkConsole('取数耗时时间：', formatTime(beginTime, endTime))
    updateAIOutlineRequestStatusRef({
      outlineLoading: false,
      outlineLoaded: true,
      referenceLoading: false,
      referenceLoaded: true,
    })
    setAiWritingLoadding(false)
    console.log(chalk.blue('完成单个写文思路的重新取数'))
  }

  // 初始化大纲和写作问题
  const initAIOutline = async () => {
    await getAIOutlineList()
    await getAIOutlineConceptByBatch()
    await getConceptAnswerByBatch()
    console.log(chalk.green('流程结束'))
    setAiWritingLoadding(false)
    updateAIOutlineRequestStatusRef({
      outlineLoading: false,
      outlineLoaded: true,
    })
  }

  // 获取知识库
  const getKnowledgeDataBase = async (text: string) => {
    try {
      const beginTime = performance.now()
      const res = await getSearchData({ text }, abortController.current.signal)
      const endTime = performance.now()
      const list =
        res.reference?.map((ele: any) => {
          return {
            id: uuidV4(),
            edited: false,
            ...ele,
          }
        }) || []
      console.log('知识库结果', res)
      elkReport({
        name: ELKReportName.aiWritingInfo,
        message: `知识库获取耗时：${formatTime(beginTime, endTime)}`,
        grade: 'Info',
        category: 'promise',
      })
      setDataBaseId(res.traceId)
      setDataBaseList(list)
    } catch (e) {
    } finally {
      setAiWritingLoadding(false)
      console.log('知识库获取结束')
    }
  }

  // 初始化流程
  const initWritingStep = () => {
    console.log('初始化流程')
    stopAIRequestRef.current = true // 主动终止任何进行中的流式接口
    setPromopt({ title: '', tone: 'neutral' })
    setWritingStep(IWritingStep.promopt)
    setDataBaseId('')
    setDataBaseList([])
    setAIOutlines([])
    initCharacterSetting() // 初始化人设
    aiOutlineRefs.current = []
  }

  return {
    writingStep,
    aiWritingLoadding,
    promopt,
    dataBaseId,
    dataBaseList,
    extraDB,
    aiOutlines,
    aiOutlineRefs,
    stopAIRequestRef,
    aiOutlineRequestStatus,
    aiOutlineRequestStatusRef,
    showOrderPop,
    leftIsEmpty,
    abortController,
    outlinePromptInfo,
    setLeftIsEmpty,
    setWritingStep,
    setAiWritingLoadding,
    setPromopt,
    setDataBaseList,
    setExtraDB,
    updateAIOutlines,
    setAiOutlineRequestStatus,
    setShowOrderPop,
    getKnowledgeDataBase,
    initWritingStep,
    updateAIOutlineRequestStatusRef,
    getAIOutlineList,
    initAIOutline,
    generateNewAIOutline,
    generateNewConceptAnswer,
    closeOutlineAsk,
    updatePrompt,
  }
}

export default useAIWritingModel
